import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import _ from "lodash";
import { kebabCaseKeys } from "src/utility/utilityFunctions";

import { useAuthUser } from "@features/auth";
import { useMutateError } from "@features/errors";
import { useLoadActiveOrderWindows } from "@features/orderCalendarMonths/useActiveOrderWindows";
import { useOptionalDistributorOrderingCtx } from "@features/orderingCtx/DistributorOrderingContext";
import { SWEEPSTAKE_ORDERING_CTX_ID } from "@features/orderingCtx/OrderingCtx";
import { Program } from "@models";
import { Brand } from "@models/Brand";
import { OrderCalendarMonth } from "@models/OrderCalendarMonth";
import { ProgramStatus } from "@models/Program";
import { Realm } from "@models/Realm";
import client from "@services/api";
import { buildPaginatedQuery } from "@utils/reactQuery";

import { useOptionalPlanningToolStore } from "../planningTool/planningToolStore";

export const programsKeyFactory = createQueryKeys("programs", {
  paginated: (params) => ({
    queryKey: [params],
    queryFn: () => client.get<Program[]>("programs", { params }),
  }),
  detail: (id: string) => ({
    queryKey: [id],
    queryFn: () =>
      client.get<Program>(`programs/${id}`).then((res) => res.data),
  }),
  list: (params) => ({
    queryKey: [params],
    queryFn: () =>
      client
        .get<Program[]>("programs", {
          params: { ...params, skipPagination: true },
        })
        .then((res) => res.data),
  }),
});

export const usePaginatedProgramsQuery = buildPaginatedQuery(
  programsKeyFactory.paginated
);

export const useProgramQuery = (id?: string | null) => {
  return useQuery({ ...programsKeyFactory.detail(id!), enabled: !!id });
};

const PROGRAM_STALE_TIME = 15 * 60 * 1000;

export const useProgramsByOcmIdQuery = (ocmId?: string | null) => {
  const { channel } = useAuthUser();
  return useQuery({
    ...programsKeyFactory.list({
      filter: {
        orderCalendarMonthIds: [ocmId],
        isPreOrderActive: true,
        isPreOrder: true,
        isCanceled: false,
        channel,
        accessType: "internal",
      },
    }),
    enabled: !!ocmId,
    // we don't expect these to change
    staleTime: PROGRAM_STALE_TIME,
  });
};

export type ProgramWithTargetOCM = Program & {
  targetOCM: OrderCalendarMonth;
};

type OCMProgramVisibility = "active" | "visible";

const useOCMProgramQueryContext = () => {
  const { channel, territory, realm } = useAuthUser();
  const distCtx = useOptionalDistributorOrderingCtx();

  return {
    channel,
    territory,
    realm,
    isDistSweepstakes: distCtx?.orderingCtx?.id === SWEEPSTAKE_ORDERING_CTX_ID,
  };
};

const buildOCMProgramsQuery = (
  ocmId: string,
  visibility: OCMProgramVisibility,
  {
    channel,
    territory,
    realm,
    isDistSweepstakes,
  }: ReturnType<typeof useOCMProgramQueryContext>
) => ({
  ...programsKeyFactory.list({
    filter: {
      orderCalendarMonthIds: [ocmId],
      [visibility === "active" ? "isPreOrderActive" : "isPreOrderVisible"]:
        true,
      isPreOrder: true,
      isCanceled: false,
      ...(realm !== Realm.Distributor && {
        channel,
        territoryId: territory,
      }),
      accessType: realm === Realm.Distributor ? "distributor" : "internal",
      ...(isDistSweepstakes && { isCouponOrSweepstakes: true }),
    },
  }),
  staleTime: PROGRAM_STALE_TIME,
});

export const useProgramsForOCMQuery = (
  ocmId: string,
  visibility: OCMProgramVisibility
) => {
  const queryContext = useOCMProgramQueryContext();

  return useQuery(buildOCMProgramsQuery(ocmId, visibility, queryContext));
};

export const useProgramsForOCMsQuery = (
  ocms: OrderCalendarMonth[],
  visibility: OCMProgramVisibility
) => {
  const queryContext = useOCMProgramQueryContext();

  return useQueries({
    queries: ocms.map((ocm) =>
      buildOCMProgramsQuery(ocm.id, visibility, queryContext)
    ),
    combine: (data) => {
      return {
        isPending: data.some((query) => query.isPending),
        error: data.find((query) => query.error)?.error ?? null,
        data: data.flatMap((query, index) => {
          const targetOCM = ocms[index];
          return (query.data ?? []).map((program) => ({
            ...program,
            targetOCM,
          }));
        }),
      };
    },
  });
};

export const useOCMProgramsQuery = (
  visibility: OCMProgramVisibility,
  orderWindowType?: "national" | "secondary"
) => {
  // Since a program can have multiple ocms for each item-program, we need to first find the ocms
  // that are orderable, and then find that ocm's programs. This means that if there are multiple orderable ocms
  // a program may appear twice in this list, once for each ocm.
  const ocmParams =
    visibility === "visible"
      ? {
          startDate: "visibleDate" as const,
          endDate: "orderWindowOpenDate" as const,
        }
      : undefined;
  const {
    data: activeOrderWindows,
    error: orderWindowsError,
    isFetching: areOrderWindowsFetching,
  } = useLoadActiveOrderWindows(ocmParams);
  const orderWindows = orderWindowType
    ? activeOrderWindows.filter(
        (orderWindow) => orderWindow.orderWindowType === orderWindowType
      )
    : activeOrderWindows;
  const programsQuery = useProgramsForOCMsQuery(orderWindows, visibility);

  return {
    ...programsQuery,
    error: orderWindowsError ?? programsQuery.error,
    isPending: areOrderWindowsFetching || programsQuery.isPending,
  };
};

/*
 * Planning-tool program mutations
 */

export type SaveProgramPayload = {
  id: string;
  brands?: Pick<Brand, "id">[];
  primaryBrand?: string | Pick<Brand, "id">;
  /** territory ids */
  territories?: string[];
  /** state ids */
  states?: string[];
  anaplanProgramId?: string | null;
  /** every other key is serialized as a JSON:API attribute */
  [attribute: string]: any;
};

/**
 * Splits a flat payload into JSON:API attributes + relationships for the
 * jsona serializer (relationship values carry a `__type`, and are listed in
 * `relationshipNames` so they are not serialized as attributes).
 */
const buildProgramBody = ({
  id: _id,
  brands,
  primaryBrand,
  territories,
  states,
  anaplanProgramId,
  ...attributes
}: SaveProgramPayload) => {
  const primaryBrandId =
    typeof primaryBrand === "string" ? primaryBrand : primaryBrand?.id;
  const relationships = {
    ...(brands && {
      brands: brands.map(({ id }) => ({ __type: "brand", id })),
    }),
    ...(primaryBrandId && {
      primaryBrand: { __type: "brand", id: primaryBrandId },
    }),
    ...(territories && {
      territories: territories.map((id) => ({ __type: "territory", id })),
    }),
    ...(states && {
      states: states.map((id) => ({ __type: "state", id })),
    }),
    anaplanPrograms: anaplanProgramId
      ? [{ __type: "anaplanProgram", id: anaplanProgramId }]
      : [],
    ...(attributes.orderType === "pre-order" &&
      attributes.orderCalendarMonthId && {
        orderCalendarMonth: {
          __type: "orderCalendarMonth",
          id: attributes.orderCalendarMonthId,
        },
      }),
  };

  return {
    __type: "program",
    ...attributes,
    ...relationships,
    relationshipNames: Object.keys(relationships),
  };
};

const useSetProgramCache = () => {
  const queryClient = useQueryClient();
  return (program: Program) => {
    queryClient.setQueryData(
      programsKeyFactory.detail(program.id).queryKey,
      program
    );
    queryClient.invalidateQueries({
      queryKey: programsKeyFactory.paginated._def,
    });
    queryClient.invalidateQueries({ queryKey: programsKeyFactory.list._def });
  };
};

export type CreateProgramPayload = Omit<SaveProgramPayload, "id"> & {
  name: string;
  channel: string;
  orderType: string;
  programTypeId: string;
  orderCalendarMonthId?: string | null;
};

export const useCreateProgramMutation = () => {
  const setProgramCache = useSetProgramCache();
  const setError = useMutateError();
  return useMutation({
    mutationFn: ({
      orderCalendarMonthId,
      ...payload
    }: CreateProgramPayload) => {
      // an order window is only assigned on national (type 1) pre-orders
      const includeOrderWindow =
        payload.orderType === "pre-order" && payload.programTypeId === "1";
      return client
        .post<Program>(
          "programs",
          buildProgramBody({
            id: "",
            status: "draft",
            ...payload,
            ...(includeOrderWindow && { orderCalendarMonthId }),
          })
        )
        .then((res) => res.data);
    },
    onSuccess: setProgramCache,
    onError: (err) => setError(err, "Create Program"),
  });
};

export const useUpdateProgramMutation = () => {
  const setProgramCache = useSetProgramCache();
  const setError = useMutateError();
  return useMutation({
    mutationFn: (payload: SaveProgramPayload) =>
      client
        .put<Program>(`programs/${payload.id}`, buildProgramBody(payload))
        .then((res) => res.data),
    onSuccess: setProgramCache,
    onError: (err) => setError(err, "Program Update"),
  });
};

/**
 * Submitting can be rejected with per-item-program validation errors. When
 * used inside a CurrentProgramProvider those are stored on the context (which
 * pops the offending rows open); anything else surfaces as an error toast.
 */
export const useSubmitProgramMutation = () => {
  const setProgramCache = useSetProgramCache();
  const setError = useMutateError();
  const planningToolStore = useOptionalPlanningToolStore();
  return useMutation({
    mutationFn: (payload: SaveProgramPayload) =>
      client
        .post<Program>(
          `programs/${payload.id}/submit`,
          buildProgramBody(payload)
        )
        .then((res) => res.data),
    onSuccess: (program) => {
      setProgramCache(program);
      planningToolStore?.getState().clearSubmitErrors();
    },
    onError: (err) => {
      const submitErrors = parseProgramSubmitErrors(err);
      if (submitErrors && planningToolStore) {
        planningToolStore.getState().setSubmitErrors(submitErrors);
      } else {
        setError(err, "Submitting Program");
      }
    },
  });
};

export type CloneProgramPayload = {
  parentProgramId: string;
  name: string;
  channel: string;
  orderType: string;
  programTypeId: string | number;
  brandAssignmentLevel: string;
  brands?: Pick<Brand, "id">[];
  primaryBrand: string;
  accessType?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  orderCalendarMonthId?: string | null;
  anaplanProgramId?: string | null;
  /** Required by the API when the in-market date is in January. */
  budgetYearOverride?: string;
};

export const useCloneProgramMutation = () => {
  const setProgramCache = useSetProgramCache();
  const setError = useMutateError();
  return useMutation({
    // the clone endpoint takes a plain kebab-cased body, not JSON:API
    mutationFn: ({
      parentProgramId,
      anaplanProgramId,
      programTypeId,
      ...payload
    }: CloneProgramPayload) =>
      client
        .post<Program>(
          `programs/${parentProgramId}/clone`,
          kebabCaseKeys({
            ...payload,
            programTypeId: +programTypeId,
            ...(anaplanProgramId && { anaplanProgramsIds: [anaplanProgramId] }),
          }),
          { serializeBody: false }
        )
        .then((res) => res.data),
    onSuccess: setProgramCache,
    onError: (err) => setError(err, "Program Copy"),
  });
};

export const useCancelProgramMutation = () => {
  const queryClient = useQueryClient();
  const setError = useMutateError();
  return useMutation({
    mutationFn: (programId: string) =>
      client.post(`programs/${programId}/cancel`, {}),
    onSuccess: (_res, programId) => {
      queryClient.setQueryData<Program>(
        programsKeyFactory.detail(programId).queryKey,
        (program) => program && { ...program, status: ProgramStatus.Canceled }
      );
      queryClient.invalidateQueries({
        queryKey: programsKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({ queryKey: programsKeyFactory.list._def });
    },
    onError: (err) => setError(err, "Program Cancel"),
  });
};

export type ProgramSubmitError = { name: string; message: string };
/** Validation errors from submitting a program, keyed by item-program id. */
export type ProgramSubmitErrors = Record<string, ProgramSubmitError[]>;

/**
 * A program submit can be rejected with per-item-program validation errors
 * (422 with `meta.item_program_id` on each error). Returns null when the
 * error is not of that shape, in which case it should be surfaced as a
 * regular mutation error.
 */
export const parseProgramSubmitErrors = (
  error: unknown
): ProgramSubmitErrors | null => {
  if (!isAxiosError(error)) return null;
  const errors = error.response?.data?.errors;
  if (!Array.isArray(errors) || !errors[0]?.meta?.item_program_id) return null;

  return _(errors)
    .groupBy((err) => err.meta.item_program_id)
    .mapValues((errs) =>
      errs.map((err) => ({
        name: _.camelCase(err.source),
        message: err.detail,
      }))
    )
    .value();
};
